import { IT_VERTICAL_SHORT_LABEL, formatOfferVersion } from '@devizy/shared';
import { Button } from '@devizy/ui';
import { Eye, LockSimple } from '@phosphor-icons/react';
import { Link, Navigate, createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { PageHeader } from '@/components/dashboard/page-header';
import { DataTable, StatusBadge, type DataTableColumn } from '@/components/data-table';
import { OfferDetailActions } from '@/components/offer/offer-detail-actions';
import { OfferPaymentBanner } from '@/components/offer/offer-payment-banner';
import {
  VersionSnapshotDialog,
  type VersionSnapshotRef,
} from '@/components/offer/version-snapshot-dialog';
import { trpc } from '@/lib/trpc/client';

import type { OfferLanguage } from '@devizy/shared';

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Brouillon',
  PUBLISHED: 'Publiée',
  SELECTION_IN_PROGRESS: 'En sélection',
  AWAITING_PAYMENT: 'En attente de paiement',
  PAYMENT_ISSUE: 'Défaut de paiement',
  ACTIVE: 'Active',
  EXPIRED_NO_SELECTION: 'Expirée sans sélection',
  CLOSED_NO_WINNER: 'Close',
  ARCHIVED: 'Archivée',
};

const SIZE_LABEL: Record<string, string> = {
  POC: 'Proof of concept',
  MEDIUM: 'Moyen',
  LARGE: 'Grand',
  LONG_TERM: 'Long terme',
};

const MODE_LABEL: Record<string, string> = {
  TIME_BASED_DAY: 'Au jour (TJ)',
  TIME_BASED_HOUR: "À l'heure (TH)",
  FIXED: 'Forfait',
  PER_LINE: 'Prix par ligne',
};

const BIDDER_PREF_LABEL: Record<string, string> = {
  INDIVIDUAL: 'Freelance individuel',
  COMPANY: 'Société',
  ANY: 'Les deux',
};

const VERSION_COLUMNS: DataTableColumn<VersionSnapshotRef>[] = [
  {
    key: 'version',
    header: 'Version',
    cell: (snap) => (
      <span className="font-mono font-medium tabular-nums">
        {formatOfferVersion(snap.version, snap.versionMinor)}
      </span>
    ),
  },
  {
    key: 'changeType',
    header: 'Type',
    cell: (snap) => (
      <StatusBadge
        label={snap.changeType === 'MAJOR' ? 'Majeure' : 'Mineure'}
        tone={snap.changeType === 'MAJOR' ? 'primary' : 'neutral'}
      />
    ),
  },
  {
    key: 'createdAt',
    header: 'Date',
    align: 'right',
    cell: (snap) => (
      <span className="text-muted-foreground font-mono text-xs tabular-nums">
        {snap.createdAt.toLocaleString('fr-FR')}
      </span>
    ),
  },
];

function statusBadgeClass(status: string): string {
  if (status === 'PUBLISHED' || status === 'ACTIVE') return 'bg-success/10 text-success';
  if (status === 'SELECTION_IN_PROGRESS') return 'bg-primary/10 text-primary';
  if (status === 'DRAFT') return 'bg-muted text-muted-foreground';
  if (status === 'PAYMENT_ISSUE') return 'bg-destructive/12 text-destructive-strong';
  return 'bg-warning/12 text-warning-strong';
}

function formatBudget(amount: number | null, currency: string | null): string {
  if (amount === null) return 'Non renseigné';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency ?? 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

function StatTile({
  label,
  value,
  hint,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  mono?: boolean;
}): React.ReactElement {
  return (
    <div className="border-border bg-background rounded-xl border p-4">
      <dt className="text-muted-foreground text-[11px] font-medium tracking-wider uppercase">
        {label}
      </dt>
      <dd className={`mt-1.5 text-sm font-medium ${mono ? 'font-mono tabular-nums' : ''}`}>
        {value}
      </dd>
      {hint ? <dd className="text-muted-foreground mt-1 text-xs">{hint}</dd> : null}
    </div>
  );
}

const CARD =
  'border-border bg-card rounded-2xl border p-6 shadow-[0_8px_24px_-12px_hsl(30_10%_30%/0.06)]';

export const Route = createFileRoute('/_app/offers/$id/')({
  component: OfferDetailPage,
});

function OfferDetailPage(): React.ReactElement {
  const { id } = Route.useParams();
  const me = trpc.user.me.useQuery();
  const offerQuery = trpc.offer.byId.useQuery({ offerId: id });
  const versionsQuery = trpc.offer.versionHistory.useQuery({ offerId: id });
  const [viewedSnapshot, setViewedSnapshot] = React.useState<VersionSnapshotRef | null>(null);

  if (offerQuery.isLoading) {
    return <p className="text-muted-foreground text-sm">Chargement…</p>;
  }
  if (offerQuery.isError || !offerQuery.data) {
    if (me.isLoading) {
      return <p className="text-muted-foreground text-sm">Chargement…</p>;
    }
    if (me.data?.role === 'BIDDER') {
      return <Navigate to="/offers/$id/bid" params={{ id }} replace />;
    }
    return (
      <div className="border-border bg-card rounded-2xl border p-8 text-center">
        <h1 className="font-display text-base">Offre introuvable</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Cette offre n&apos;existe pas ou ne vous appartient pas.
        </p>
        <Link
          to="/offers"
          className="text-primary mt-4 inline-block text-sm font-medium hover:underline"
        >
          Retour aux offres
        </Link>
      </div>
    );
  }

  const offer = offerQuery.data;
  const primary = offer.primaryLanguage as OfferLanguage | null;
  const secondary = (offer.secondaryLanguages as OfferLanguage[] | null) ?? [];
  const parents = offer.expectations;
  const budget = offer.budgetAmount === null ? null : Number(offer.budgetAmount);
  const deadline = offer.applicationDeadline
    ? new Date(offer.applicationDeadline).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    : 'Aucune';
  const versionSnapshots: VersionSnapshotRef[] = (versionsQuery.data?.items ?? []).map((s) => ({
    id: s.id,
    version: s.version,
    versionMinor: s.versionMinor,
    changeType: s.changeType,
    createdAt: s.createdAt,
  }));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={offer.title ?? 'Offre sans titre'}
        subtitle={`${offer.currentVersion > 1 || offer.currentVersionMinor > 0 ? `Version ${formatOfferVersion(offer.currentVersion, offer.currentVersionMinor)} · ` : ''}${(IT_VERTICAL_SHORT_LABEL as Record<string, string>)[offer.vertical] ?? offer.vertical}${offer.isPrivate ? ' · privée' : ''}`}
        actions={
          <>
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(offer.status)}`}
            >
              {STATUS_LABEL[offer.status] ?? offer.status}
            </span>
            {offer.isOwner ? (
              <OfferDetailActions
                offerId={offer.id}
                status={offer.status}
                previousApplicantsCount={offer.previousApplicantsCount}
              />
            ) : null}
          </>
        }
      />

      {offer.isOwner &&
      (offer.status === 'PAYMENT_ISSUE' || offer.status === 'AWAITING_PAYMENT') ? (
        <OfferPaymentBanner offerId={offer.id} />
      ) : null}

      <section className={CARD}>
        <h2 className="font-display mb-4 text-base">Cadrage</h2>
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <StatTile
            label="Catégorie"
            value={
              (IT_VERTICAL_SHORT_LABEL as Record<string, string>)[offer.vertical] ?? offer.vertical
            }
          />
          <StatTile
            label="Grandeur"
            value={offer.size ? (SIZE_LABEL[offer.size] ?? offer.size) : '—'}
          />
          <StatTile
            label="Tarification préférée"
            value={
              offer.pricingPreference
                ? (MODE_LABEL[offer.pricingPreference] ?? offer.pricingPreference)
                : 'Indifférente'
            }
          />
          <StatTile
            label="Préférence bidder"
            value={BIDDER_PREF_LABEL[offer.bidderPreference] ?? offer.bidderPreference}
          />
          <StatTile
            label="Budget privé"
            value={formatBudget(budget, offer.budgetCurrency)}
            hint={
              <span className="inline-flex items-center gap-1">
                <LockSimple weight="bold" className="h-3 w-3" />
                Jamais visible des bidders
              </span>
            }
            mono
          />
          <StatTile label="Date limite de candidature" value={deadline} />
          <StatTile
            label="Candidatures reçues"
            value={
              <span data-testid="offer-bids-count" data-count={offer.bidsCount}>
                {offer.bidsCount}
              </span>
            }
            hint="Toutes versions confondues"
            mono
          />
          <StatTile
            label="Visibilité"
            value={offer.isPrivate ? 'Privée — sur invitation' : 'Publique'}
            hint={
              offer.isPrivate
                ? `${offer.invitationsCount} invité${offer.invitationsCount > 1 ? 's' : ''}`
                : 'Visible dans le feed'
            }
          />
        </dl>
      </section>

      <section className={CARD}>
        <h2 className="font-display mb-4 text-base">Description</h2>
        <p className="text-sm whitespace-pre-wrap">
          {offer.description || <em className="text-muted-foreground">Aucune description.</em>}
        </p>
      </section>

      <section className={CARD}>
        <h2 className="font-display mb-4 text-base">Langues du projet</h2>
        <div className="flex flex-col gap-2 text-sm">
          {primary ? (
            <p>
              <span className="font-medium">Principale :</span> {primary.code.toUpperCase()} ·{' '}
              {primary.requiredLevel}
            </p>
          ) : null}
          {secondary.length > 0 ? (
            <p>
              <span className="font-medium">Secondaires :</span>{' '}
              {secondary.map((l) => `${l.code.toUpperCase()} · ${l.requiredLevel}`).join(', ')}
            </p>
          ) : null}
          {!primary && secondary.length === 0 && (
            <p className="text-muted-foreground">Aucune langue renseignée.</p>
          )}
        </div>
      </section>

      <section className={CARD}>
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="font-display text-base">Cahier des charges</h2>
          <span className="text-muted-foreground font-mono text-xs tabular-nums">
            {parents.length} module{parents.length > 1 ? 's' : ''}
          </span>
        </div>
        {parents.length === 0 ? (
          <div className="border-border bg-card/50 rounded-xl border border-dashed p-8 text-center">
            <p className="text-muted-foreground text-sm">
              Aucun module. Modifiez l&apos;offre pour en ajouter.
            </p>
          </div>
        ) : (
          <ol className="flex flex-col gap-3">
            {parents.map((parent, index) => {
              const childCount = parent.children.length;
              return (
                <li
                  key={parent.id}
                  className="border-border bg-background flex items-start gap-4 rounded-xl border p-4"
                >
                  <span className="text-muted-foreground w-6 shrink-0 font-mono text-xs tabular-nums">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{parent.title}</p>
                    {parent.description ? (
                      <p className="text-muted-foreground mt-1 text-xs">{parent.description}</p>
                    ) : null}
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-xs tabular-nums ${childCount === 0 ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'}`}
                  >
                    {childCount}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      <section className={CARD}>
        <h2 className="font-display mb-4 text-base">Historique des versions</h2>
        <DataTable
          columns={VERSION_COLUMNS}
          rows={versionSnapshots}
          rowKey={(snap) => snap.id}
          actionHeader={<span className="sr-only">Consulter</span>}
          rowAction={(snap) => (
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => setViewedSnapshot(snap)}
            >
              <Eye weight="bold" className="mr-1" />
              Consulter
            </Button>
          )}
          emptyState={
            <p className="text-muted-foreground text-center text-sm">Aucune version publiée.</p>
          }
        />
      </section>

      <VersionSnapshotDialog
        offerId={id}
        snapshot={viewedSnapshot}
        onClose={() => setViewedSnapshot(null)}
      />
    </div>
  );
}
